import { observer } from 'mobx-react-lite';
import { Alert, Grid, Typography, Box } from '@mui/material';

import { Categories } from 'components/diligence/Categories';

import { DiligenceStore } from 'stores/diligence/DiligenceStore';

import { UploadArea } from '../UploadArea';
import { CategoryOutput } from 'dtos/shared/jobDetails';
import { FileOutput } from 'dtos/diligence/FileOutput';

type DiligenceProps = {
  diligenceStore: DiligenceStore;
};

const getUploadStatus = (uploadStatus: string) => {
  if (!uploadStatus || uploadStatus === 'none') {
    return null;
  }

  const severity = uploadStatus === 'success' ? 'success' : 'error';
  let text = '';

  switch (uploadStatus) {
    case 'success':
      text = 'Upload complete! Review your uploaded document(s) below.';
      break;

    case 'sizeError':
      text = 'Upload failed: files must be smaller than {maxUploadSizeMb} MB.';
      break;

    case 'error':
      text = 'Upload failed.';
      break;
  }

  return (
    <Alert sx={{ marginBottom: '16px' }} severity={severity}>
      {text}
    </Alert>
  );
};

const Diligence = observer(({ diligenceStore }: DiligenceProps) => {
  const { categoriesStore, filesStore, toggleCategory, setCategorySubmitted, setCategoryIncomplete } = diligenceStore;
  const { showingUploadStatus, uploadStatus, sortedFiles, uploadStore, uploadFiles, deleteFile } = filesStore;
  const { categories } = categoriesStore;
  const defaultCategoryTitles = diligenceStore.job.defaultCategories.map((category) => category.title);

  const handleDeleteFile = async (file: FileOutput, category?: CategoryOutput) => {
    await deleteFile(file, category);
    const otherFileInCategory = filesStore.files.find((file) => file.category === category?.title);
    if (category && !otherFileInCategory) {
      setCategoryIncomplete(category.title);
    }
  };

  const handleFileUploads = async (acceptedFiles: File[], rejections: any[]) => {
    await uploadFiles(acceptedFiles, rejections);
    filesStore.files.forEach((file) => {
      if (file.category) {
        setCategorySubmitted(file.category);
      }
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '16px' }}>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h4" sx={{ marginBottom: '24px' }}>
            <strong>Diligence Requested</strong>
          </Typography>

          <Categories categories={categories} onToggleCategory={(category) => toggleCategory(category.title)} />
        </Grid>
      </Grid>

      <Box sx={{ marginTop: '48px', zIndex: 1 }}>
        {showingUploadStatus && getUploadStatus(uploadStatus)}
        <UploadArea
          onDrop={(acceptedFiles, rejections) => handleFileUploads(acceptedFiles, rejections)}
          uploadStore={uploadStore}
          sx={{ zIndex: 1 }}
          files={sortedFiles}
          onDelete={handleDeleteFile}
          categories={defaultCategoryTitles}
          diligenceStore={diligenceStore}
        />
      </Box>
    </div>
  );
});

export { Diligence };
